import React, { useState, useEffect } from 'react'
import { Play, Pause, Square, RotateCcw, Settings, Maximize2 } from 'lucide-react'
import usePlayStore from '../../stores/playStore'

const PlayControls = () => {
  const [currentZoom, setCurrentZoom] = useState(1)
  const { 
    isPlaying, 
    currentTime, 
    duration, 
    setPlaying, 
    setCurrentTime,
    fieldSettings,
    updateFieldSettings,
    clearSelectedEntity
  } = usePlayStore()

  // Sync zoom state with Phaser scene
  useEffect(() => {
    const updateZoom = () => {
      if (window.phaserScene) {
        setCurrentZoom(window.phaserScene.cameras.main.zoom)
      }
    }
    
    // Update zoom initially and on interval
    updateZoom()
    const interval = setInterval(updateZoom, 100)
    
    return () => clearInterval(interval)
  }, [])

  const handlePlayPause = () => {
    const newPlayingState = !isPlaying
    setPlaying(newPlayingState)
    
    // Clear selection when starting animation
    if (newPlayingState) {
      clearSelectedEntity()
    }
  }

  const handleStop = () => {
    setPlaying(false)
    setCurrentTime(0)
    clearSelectedEntity() // Clear selection when stopping animation
  }

  const handleReset = () => {
    setCurrentTime(0)
    clearSelectedEntity() // Clear selection when resetting animation
  }

  const handleTimeChange = (e) => {
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
  }

  const handleFitToScreen = () => {
    // Directly call fitFieldToScreen on the Phaser scene
    if (window.phaserScene && window.phaserScene.fitFieldToScreen) {
      window.phaserScene.fitFieldToScreen()
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="flex items-center space-x-4">
      {/* Playback Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={handlePlayPause}
          className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        
        <button
          onClick={handleStop}
          className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600 transition-colors"
        >
          <Square size={16} />
        </button>
        
        <button
          onClick={handleReset}
          className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600 transition-colors"
        >
          <RotateCcw size={16} />
        </button>
      </div>

      {/* Time Display */}
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <span>{formatTime(currentTime)}</span>
        <span>/</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* Field Settings */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => updateFieldSettings({ showGrid: !fieldSettings.showGrid })}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
            fieldSettings.showGrid 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          Grid
        </button>
        
        <button
          onClick={() => updateFieldSettings({ showNumbers: !fieldSettings.showNumbers })}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
            fieldSettings.showNumbers 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          Numbers
        </button>
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => {
            if (window.phaserScene) {
              const currentZoom = window.phaserScene.cameras.main.zoom
              const newZoom = Math.max(0.3, currentZoom - 0.1)
              window.phaserScene.cameras.main.setZoom(newZoom)
              window.phaserScene.fieldSettings.zoom = newZoom
              setCurrentZoom(newZoom)
            }
          }}
          className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          -
        </button>
        <span className="text-xs text-gray-600 w-12 text-center">
          {Math.round(currentZoom * 100)}%
        </span>
        <button
          onClick={() => {
            if (window.phaserScene) {
              const currentZoom = window.phaserScene.cameras.main.zoom
              const newZoom = Math.min(3, currentZoom + 0.1)
              window.phaserScene.cameras.main.setZoom(newZoom)
              window.phaserScene.fieldSettings.zoom = newZoom
              setCurrentZoom(newZoom)
            }
          }}
          className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          +
        </button>
        <button
          onClick={handleFitToScreen}
          className="flex items-center space-x-1 px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 hover:bg-blue-200"
        >
          <Maximize2 size={12} />
          <span>Fit</span>
        </button>
      </div>
    </div>
  )
}

export default PlayControls
